// ========================================
// 水属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  modifyAttack,
  healLife,
  drawCards,
  applyStatusToOpponentMonster,
  applyStatusToAllOpponentMonsters,
  selectAndApplyStatusToOpponent,
} from '../effectHelpers';
import {
  submergeCard,
  surfaceCard,
  incrementCardDepth,
  getDepthBonus,
  getSurfaceCost,
} from '../deepSeaZone';
import type { DeepSeaZone, DeepSeaCard } from '../deepSeaZone/types';
import { STATUS_EFFECT_TYPES, statusEffectEngine, type PlayerStatusEffect } from '../statusEffects';
import { hasCategory } from '../../utils/helpers';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData } from '../../types';

/**
 * 水属性カードの固有効果
 */
export const waterCardEffects: CardEffectRegistry = {
  /**
   * C0000142: ブリザードマスター
   * 基本技：相手モンスターの攻撃力を300下げ、600ダメージを与える。
   * 上級技：相手モンスター1体を「凍結（攻撃力半減＋行動不能）」にする、次のターン開始時に50%で解除。
   * 召喚時効果は cardTriggers/waterCards.js に実装済み
   */
  C0000142: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    // 基本技：攻撃力300ダウン + 600ダメージ
    if (context.skillType === 'basic') {
      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 汎用パーサーにフォールバックしない
      }

      const applyBasicSkill = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const newAtk = Math.max(0, m.currentAttack - 300);
            const newHp = m.currentHp - 600;
            addLog(`${m.name}の攻撃力を300ダウン、600ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${m.name}は破壊された！`, 'damage');
              return null;
            }
            return { ...m, currentAttack: newAtk, currentHp: newHp };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyBasicSkill(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力ダウン+ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyBasicSkill(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      applyBasicSkill(validTargets[0].index);
      return true;
    }

    // 上級技：凍結付与（攻撃力半減＋行動不能、50%解除）
    if (context.skillType === 'advanced') {
      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 汎用パーサーにフォールバックしない
      }

      const applyFreeze = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        // 凍結付与（攻撃力半減＋行動不能、次のターン開始時50%解除）
        applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.FREEZE, {
          duration: -1, // 永続（解除されるまで）
          removeChance: 0.5, // 50%でターン開始時に解除
        }, 'ブリザードマスター');
        addLog(`${targetMonster.name}を凍結させた！`, 'damage');
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyFreeze(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '凍結させる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyFreeze(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      applyFreeze(validTargets[0].index);
      return true;
    }

    return false;
  },

  // C0000147 (ブリザードキャット・シャード) は
  // cardTriggers/waterCards.js に実装済み（召喚時トリガーとして）

  /**
   * C0000039: アクア・メイデン
   * 基本技：相手モンスター1体を「眠り」状態にする。
   * 召喚時効果は waterCards.js のトリガーとして実装済み
   */
  C0000039: (skillText, context) => {
    if (context.skillType === 'basic') {
      // 眠り: 次のターン終了時まで行動不能＋効果無効、ターン開始時50%解除
      selectAndApplyStatusToOpponent(
        context,
        STATUS_EFFECT_TYPES.SLEEP,
        {
          duration: 2, // 次のターン終了時まで
          removeChance: 0.5, // 50%で解除
        },
        'アクア・メイデン'
      );
      return true;
    }
    return false;
  },

  /**
   * C0000045: 海流の守護者
   * 基本技：自分の水属性モンスター1体の状態異常を回復する。
   */
  C0000045: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {

      // 自分の水属性モンスターを取得
      const waterMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.attribute === '水');

      if (waterMonsters.length === 0) {
        addLog('自分のフィールドに水属性モンスターがいません', 'info');
        return true; // 技は発動したが効果なし（汎用パーサーへのフォールバック防止）
      }

      // 状態異常を持つ水属性モンスターをフィルタ
      const afflictedMonsters = waterMonsters.filter(
        ({ monster }) => monster && monster.statusEffects && monster.statusEffects.length > 0
      );

      if (afflictedMonsters.length === 0) {
        addLog('状態異常を持つ水属性モンスターがいません', 'info');
        return true; // 技は発動したが効果なし（汎用パーサーへのフォールバック防止）
      }

      const clearStatusEffects = (targetIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target && target.statusEffects && target.statusEffects.length > 0) {
            addLog(`${target.name}の状態異常を全て回復！`, 'heal');
            newField[targetIndex] = {
              ...target,
              statusEffects: [],
            };
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (afflictedMonsters.length === 1) {
        clearStatusEffects(afflictedMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '状態異常を回復する水属性モンスターを選択',
          validTargets: afflictedMonsters.map(t => t.index),
          targetPlayer: 'self',
          callback: (selectedIndex: number) => {
            clearStatusEffects(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      clearStatusEffects(afflictedMonsters[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000148: 氷の双尾猫
   * 基本技：1度のみ使用可能、手札を1枚捨て自分の墓地の《ブリザードキャット》1体を場に戻す（HP半分）
   */
  C0000148: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {

      if (myHand.length > 0) {
        // 手札を1枚捨てる
        const discardedCard = myHand[0];
        setMyHand(prev => prev.slice(1));
        setMyGraveyard(prev => [...prev, discardedCard]);
        addLog(`手札から「${discardedCard.name}」を墓地に送った`, 'info');

        // 墓地からブリザードキャットを蘇生（HP半減）
        return reviveFromGraveyard(context, (card) => {
          return card.name?.includes('ブリザードキャット') ?? false;
        }, { hpHalf: true }); // HP半分、攻撃力は元のまま
      } else {
        addLog('手札がありません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000313: アクア・リヴァイアサン
   * 【召喚時】相手の場にいるモンスター全ての攻撃力をターン終了時まで500下げる
   * 上級技: SP1を払い、次の相手ターン開始フェイズに相手のSPトークンを１つレスト状態にする
   */
  C0000313: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, myActiveSP, setMyActiveSP, setMyRestedSP, opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {

      const monsters = opponentField.filter(m => m !== null);
      if (monsters.length === 0) {
        addLog('相手モンスターがいません', 'info');
        return true;
      }

      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newAtk = Math.max(0, (m.currentAttack ?? m.attack ?? 0) - 500);
          return { ...m, currentAttack: newAtk };
        }
        return m;
      }));

      addLog('相手モンスター全体の攻撃力を500ダウン', 'info');
      return true;
    }

    // 上級技: SP1を払い、次の相手ターン開始フェイズに相手のSPトークンを１つレスト状態にする
    if (context.skillType === 'advanced') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      // SP1を追加コストとして払う
      if (myActiveSP < 1) {
        addLog('SPが足りません（1必要）', 'info');
        return false;
      }

      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      // 次の相手ターン開始時にSPをレスト状態にするフラグを設定
      addLog('アクア・リヴァイアサンの上級技: 次の相手ターン開始時、相手のSP1個をレスト状態にする！', 'info');

      // 遅延効果のためにゲームフラグを設定
      // pendingOpponentSPRest: 効果対象のプレイヤー番号（相手）
      const opponentPlayer = currentPlayer === 1 ? 2 : 1;
      if (context.setGameFlags) {
        context.setGameFlags({ pendingOpponentSPRest: opponentPlayer });
      }

      return true;
    }

    return false;
  },

  /**
   * C0000330: アクアレギナの漂流漁師
   * 【召喚時】墓地の『アクアレギア』と名の付くモンスター1体をデッキに戻すと、レスト状態のSPトークンをアクティブにする
   */
  C0000330: (skillText, context) => {
    const { addLog } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck, myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {

      const aquaCard = myGraveyard.find(card =>
        card.name && card.name.includes('アクアレギア')
      );

      if (aquaCard) {
        // デッキに戻す
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== aquaCard.uniqueId));
        setMyDeck(prev => [...prev, aquaCard]);
        addLog(`墓地から「${aquaCard.name}」をデッキに戻した`, 'info');

        // SPをアクティブに
        if (myRestedSP > 0) {
          setMyActiveSP(prev => prev + 1);
          setMyRestedSP(prev => prev - 1);
          addLog('レストSPを1個アクティブにした', 'info');
        }
        return true;
      } else {
        addLog('墓地にアクアレギアモンスターがありません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000150: 氷の吐息
   * 相手モンスター1体の攻撃力を800下げ、ターン終了時まで「凍結（攻撃力半減＋行動不能）」にする
   * 相手はターン開始時にSP1支払いで解除可能
   */
  C0000150: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 対象となる相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('相手フィールドにモンスターがいません', 'info');
      return true; // 対象がいなくても魔法発動自体は成功として扱う
    }

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      const targetIndex = validTargets[0].index;
      const targetMonster = validTargets[0].monster;
      if (!targetMonster) return true;

      // ATK-800を適用
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const newAtk = Math.max(0, newField[targetIndex].currentAttack - 800);
          newField[targetIndex] = {
            ...newField[targetIndex],
            currentAttack: newAtk,
          };
        }
        return newField;
      });
      addLog(`${targetMonster.name}の攻撃力を800ダウン`, 'damage');

      // 凍結を付与（duration: 0 = ターン終了時まで、sp1Remove: SP1で解除可能）
      applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.FREEZE, {
        duration: 0, // ターン終了時に自動解除
        removeChance: 0, // 確率解除なし
        sp1Remove: true, // SP1支払いで解除可能フラグ
      }, '氷の吐息');

      return true;
    }

    // 複数体の場合は選択UI表示
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '凍結を与える相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          const targetMonster = opponentField[selectedIndex];
          if (!targetMonster) return;

          // ATK-800を適用
          setOpponentField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              const newAtk = Math.max(0, newField[selectedIndex].currentAttack - 800);
              newField[selectedIndex] = {
                ...newField[selectedIndex],
                currentAttack: newAtk,
              };
            }
            return newField;
          });
          addLog(`${targetMonster.name}の攻撃力を800ダウン`, 'damage');

          // 凍結を付与
          applyStatusToOpponentMonster(context, selectedIndex, STATUS_EFFECT_TYPES.FREEZE, {
            duration: 0,
            removeChance: 0,
            sp1Remove: true,
          }, '氷の吐息');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // setPendingMonsterTargetがない場合は最初の対象を選択
    const targetIndex = validTargets[0].index;
    const targetMonster = validTargets[0].monster;
    if (!targetMonster) return true;

    setOpponentField(prev => {
      const newField = [...prev];
      if (newField[targetIndex]) {
        const newAtk = Math.max(0, newField[targetIndex].currentAttack - 800);
        newField[targetIndex] = {
          ...newField[targetIndex],
          currentAttack: newAtk,
        };
      }
      return newField;
    });
    addLog(`${targetMonster.name}の攻撃力を800ダウン`, 'damage');

    applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.FREEZE, {
      duration: 0,
      removeChance: 0,
      sp1Remove: true,
    }, '氷の吐息');

    return true;
  },

  /**
   * C0000051: リヴァイアサンの奔流
   * 相手モンスター全体に、場にいる水属性モンスターの数×400ダメージを与える
   */
  C0000051: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setOpponentField } = getPlayerContext(context);

    // 水属性モンスターの数をカウント
    const waterCount = myField.filter(m =>
      m && m.attribute === '水'
    ).length;

    if (waterCount === 0) {
      addLog('水属性モンスターがいないためダメージなし', 'info');
      return true;
    }

    const damage = waterCount * 400;
    addLog(`水属性モンスター×${waterCount}で${damage}ダメージ！`, 'damage');

    // 相手モンスター全体にダメージ
    const opponentMonsters = opponentField.filter(m => m !== null);
    if (opponentMonsters.length === 0) {
      addLog('相手モンスターがいません', 'info');
      return true;
    }

    // ダメージ計算とログ出力を先に実行
    const damageResults = opponentField.map(m => {
      if (m) {
        const newHp = Math.max(0, m.currentHp - damage);
        addLog(`${m.name}に${damage}ダメージ（残りHP: ${newHp}）`, 'damage');
        return { monster: m, newHp };
      }
      return null;
    });

    setOpponentField(prev => prev.map((m, idx) => {
      if (m && damageResults[idx]) {
        return { ...m, currentHp: damageResults[idx].newHp };
      }
      return m;
    }));

    return true;
  },

  /**
   * C0000151: ブリザードの呼び声
   * デッキからコスト3以下の《ブリザードキャット》モンスター1体を場に召喚（コスト不要）
   */
  C0000151: (skillText, context) => {
    const { addLog, setPendingDeckReview, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField, currentPlayer } = getPlayerContext(context);

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('場が満杯で召喚できません', 'info');
      return false;
    }

    // デッキからブリザードキャットを検索（条件に合うもの全て）
    const targetFilter = (card: CardWithId): boolean =>
      card.type === 'monster' &&
      card.cost <= 3 &&
      (card.name?.includes('ブリザードキャット') ?? false);

    const matchingCards = myDeck.filter(targetFilter);

    if (matchingCards.length === 0) {
      addLog('条件に合うブリザードキャットがデッキにいません', 'info');
      return false;
    }

    // 召喚処理を共通化
    const summonMonster = (target: CardWithId) => {
      setMyDeck(prev => prev.filter(c => c.uniqueId !== target.uniqueId));
      setMyField(prev => {
        const newField = [...prev];
        // 最新の空きスロットを再確認
        const slotIndex = newField.findIndex(slot => slot === null);
        if (slotIndex === -1) {
          addLog('場が満杯で召喚できません', 'info');
          return prev;
        }
        newField[slotIndex] = {
          ...target,
          currentHp: target.hp ?? 0,
          currentAttack: target.attack ?? 0,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          charges: [],
          statusEffects: [],
          owner: currentPlayer,
        };
        return newField;
      });
      addLog(`ブリザードの呼び声: デッキから「${target.name}」を特殊召喚！`, 'heal');
    };

    // 1枚のみの場合は自動選択
    if (matchingCards.length === 1) {
      summonMonster(matchingCards[0]);
      return true;
    }

    // 複数ある場合はデッキ選択UIを表示
    if (setPendingDeckReview) {
      setPendingDeckReview({
        cards: matchingCards,
        title: 'ブリザードの呼び声',
        message: '場に召喚するコスト3以下の《ブリザードキャット》を1体選択してください',
        allowReorder: false,
        selectMode: {
          enabled: true,
          count: 1,
        },
        onSelect: (selectedCards: CardWithId[]) => {
          if (selectedCards.length > 0) {
            summonMonster(selectedCards[0]);
          }
        },
        onCancel: () => {
          addLog('ブリザードの呼び声: キャンセルしました', 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初の1枚を選択
    summonMonster(matchingCards[0]);
    return true;
  },

  /**
   * C0000154: ブリザードの覚醒
   * 場にいる《ブリザードキャット》モンスター1体の攻撃力をターン終了時まで倍にし、
   * 相手モンスターへの攻撃を2回行えるようにする
   */
  C0000154: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // ブリザードキャットを取得
    const blizzardCats = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('ブリザードキャット'));

    if (blizzardCats.length === 0) {
      addLog('場にブリザードキャットがいません', 'info');
      return false;
    }

    const applyAwakening = (targetIndex: number) => {
      setMyField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const newAtk = m.currentAttack * 2;
          addLog(`${m.name}の攻撃力が${newAtk}に倍増！`, 'heal');
          addLog(`${m.name}は2回攻撃可能！`, 'heal');
          return {
            ...m,
            currentAttack: newAtk,
            attacksRemaining: 2, // 2回攻撃可能
            doubleAttack: true,  // 2回攻撃フラグ
          };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (blizzardCats.length === 1) {
      applyAwakening(blizzardCats[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '覚醒させるブリザードキャットを選択してください',
        targetPlayer: 'self',
        callback: (selectedIndex: number) => {
          applyAwakening(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック：最初のブリザードキャットを選択
    applyAwakening(blizzardCats[0].index);
    return true;
  },

  /**
   * C0000152: 凍てつく爪痕
   * 場にいる【ビースト・猫】1体の攻撃力分のダメージを相手モンスターに与える
   */
  C0000152: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 自分の【ビースト・猫】を探す
    const catTargets = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && hasCategory(monster, '【ビースト・猫】'));

    if (catTargets.length === 0) {
      addLog('凍てつく爪痕: 【ビースト・猫】モンスターがいません', 'info');
      return false;
    }

    // 相手モンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('凍てつく爪痕: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const executeEffect = (catIndex: number, targetIndex: number) => {
      const catMonster = myField[catIndex];
      if (!catMonster) return;

      const damage = catMonster.currentAttack ?? catMonster.attack ?? 0;

      // 相手モンスターにダメージ
      const targetMonster = opponentField[targetIndex];
      if (targetMonster) {
        const newHp = targetMonster.currentHp - damage;
        addLog(`凍てつく爪痕: ${catMonster.name}の攻撃力${damage}で${targetMonster.name}を攻撃！`, 'damage');
        if (newHp <= 0) {
          setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
          setOpponentGraveyard(prev => [...prev, targetMonster]);
          addLog(`${targetMonster.name}は破壊された！`, 'damage');
        } else {
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              return { ...m, currentHp: newHp };
            }
            return m;
          }));
        }
      }
    };

    // 猫が1体、相手も1体の場合は自動選択
    if (catTargets.length === 1 && opponentMonsters.length === 1) {
      executeEffect(catTargets[0].index, opponentMonsters[0].index);
      return true;
    }

    // 選択UI（猫選択 → 相手モンスター選択の2段階）
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を参照する【ビースト・猫】を選択',
        targetPlayer: 'self',
        callback: (catIndex: number) => {
          const catMonster = myField[catIndex];
          if (!catMonster) return;

          // 次に相手モンスターを選択
          setPendingMonsterTarget({
            message: `${catMonster.currentAttack ?? catMonster.attack}ダメージを与える相手モンスターを選択`,
            targetPlayer: 'opponent',
            callback: (targetIndex: number) => {
              executeEffect(catIndex, targetIndex);
            },
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeEffect(catTargets[0].index, opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000153: 氷猫の遊戯
   * 場に《ブリザードキャット》モンスターがいるとき、コスト3以下の相手モンスター1体のコントロールをターン終了時まで奪う
   * 【刹那詠唱】
   */
  C0000153: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 自分の場に《ブリザードキャット》がいるか
    const hasBlizzardCat = myField.some(m => m && m.name && m.name.includes('ブリザードキャット'));
    if (!hasBlizzardCat) {
      addLog('氷猫の遊戯: 場に《ブリザードキャット》がいません', 'info');
      return false;
    }

    // 自分のフィールドに空きがあるか
    const emptySlot = myField.findIndex(slot => slot === null);
    if (emptySlot === -1) {
      addLog('氷猫の遊戯: フィールドに空きがありません', 'info');
      return false;
    }

    // コスト3以下の相手モンスターを探す
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.cost <= 3);

    if (validTargets.length === 0) {
      addLog('氷猫の遊戯: コスト3以下の相手モンスターがいません', 'info');
      return false;
    }

    const executeControl = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // 相手フィールドから削除
      setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));

      // 自分のフィールドに配置（コントロール奪取）
      const controlledMonster = {
        ...targetMonster,
        canAttack: true,
        controlledUntilEndTurn: true, // ターン終了時に戻すフラグ
        originalOwner: targetMonster.owner,
        originalSlot: targetIndex,
      };

      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = controlledMonster;
        }
        return newField;
      });

      addLog(`氷猫の遊戯: ${targetMonster.name}のコントロールを奪った！`, 'info');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      executeControl(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'コントロールを奪う相手モンスターを選択（コスト3以下）',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          executeControl(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeControl(validTargets[0].index);
    return true;
  },

  /**
   * C0000159: 潮の還流
   * 自分の墓地の水属性モンスター2体をデッキに戻す。その後1枚ドローする
   */
  C0000159: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck } = getPlayerContext(context);

    // 墓地から水属性モンスターを探す
    const waterMonsters = myGraveyard.filter(card =>
      card.type === 'monster' && card.attribute === '水'
    );

    if (waterMonsters.length < 2) {
      addLog('潮の還流: 墓地に水属性モンスターが2体以上いません', 'info');
      return false;
    }

    const executeEffect = (selectedCards: CardWithId[]) => {
      // 選択したカードを墓地から削除してデッキに戻す
      const selectedIds = selectedCards.map(c => c.uniqueId);
      setMyGraveyard(prev => prev.filter(c => !selectedIds.includes(c.uniqueId)));
      setMyDeck(prev => [...prev, ...selectedCards]);

      addLog(`潮の還流: ${selectedCards.map(c => c.name).join('、')}をデッキに戻した`, 'info');

      // 1枚ドロー
      drawCards(context, 1);
    };

    // 2体のみの場合は自動選択
    if (waterMonsters.length === 2) {
      executeEffect(waterMonsters);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingGraveyardSelection) {
      setPendingGraveyardSelection({
        cards: waterMonsters,
        count: 2,
        message: 'デッキに戻す水属性モンスターを2体選択',
        callback: (selectedCards: CardWithId[]) => {
          executeEffect(selectedCards);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初の2枚を選択
    executeEffect(waterMonsters.slice(0, 2));
    return true;
  },

  /**
   * C0000288: 酸の霧
   * 相手モンスター1体の攻撃力をターン終了時まで0にする
   * 【刹那詠唱】
   */
  C0000288: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('相手フィールドにモンスターがいません', 'info');
      return true; // 対象がいなくても魔法発動自体は成功として扱う
    }

    const applyAcidFog = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          addLog(`${m.name}の攻撃力を0にした！`, 'damage');
          return {
            ...m,
            currentAttack: 0,
            atkZeroUntilEndOfTurn: true, // ターン終了時までATK0
          };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyAcidFog(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を0にする相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyAcidFog(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyAcidFog(validTargets[0].index);
    return true;
  },

  /**
   * C0000344: シスラゴンの汚染
   * 相手モンスター1体の攻撃力を1000下げ、次のターン終了時まで行動不能にする
   * 場に『ヴェルゼファール』モンスターがいる場合、この効果をターン終了時まで全体に変更
   */
  C0000344: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField } = getPlayerContext(context);

    // ヴェルゼファールがいるかチェック
    const hasVerzefaal = myField.some(m =>
      m && m.name && m.name.includes('ヴェルゼファール')
    );

    // 相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('相手フィールドにモンスターがいません', 'info');
      return true; // 対象がいなくても魔法発動自体は成功として扱う
    }

    if (hasVerzefaal) {
      // 全体効果（ターン終了時まで）
      addLog('ヴェルゼファールの力で全体に効果！', 'info');
      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newAtk = Math.max(0, m.currentAttack - 1000);
          addLog(`${m.name}の攻撃力を1000ダウン！`, 'damage');
          return { ...m, currentAttack: newAtk };
        }
        return m;
      }));
      // 全体にスタン（ターン終了時まで = duration: 0）
      applyStatusToAllOpponentMonsters(context, STATUS_EFFECT_TYPES.STUN, {
        duration: 0, // ターン終了時まで
      }, 'シスラゴンの汚染');
      return true;
    }

    // 単体効果（次のターン終了時まで）
    const applySislagonPollution = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const newAtk = Math.max(0, m.currentAttack - 1000);
          addLog(`${m.name}の攻撃力を1000ダウン！`, 'damage');
          return { ...m, currentAttack: newAtk };
        }
        return m;
      }));
      // スタン付与（次のターン終了時まで = duration: 1）
      applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.STUN, {
        duration: 1, // 次のターン終了時まで
      }, 'シスラゴンの汚染');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applySislagonPollution(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '汚染する相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applySislagonPollution(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applySislagonPollution(validTargets[0].index);
    return true;
  },

  /**
   * C0000402: 呪術狩りの封印鎖
   * 相手のモンスター1体をターン終了時まで行動不能にする
   * 闇属性モンスターの場合、次のターン終了時まで行動不能にして、攻撃力を0にする
   * 【刹那詠唱】
   */
  C0000402: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('相手フィールドにモンスターがいません', 'info');
      return true; // 対象がいなくても魔法発動自体は成功として扱う
    }

    const applySealingChain = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      const isDark = targetMonster.attribute === '闇';

      if (isDark) {
        // 闇属性：次のターン終了時まで行動不能 + ATK0
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            addLog(`${m.name}（闇属性）の攻撃力を0にした！`, 'damage');
            return {
              ...m,
              currentAttack: 0,
            };
          }
          return m;
        }));
        applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.STUN, {
          duration: 1, // 次のターン終了時まで
        }, '呪術狩りの封印鎖');
      } else {
        // 通常：ターン終了時まで行動不能
        applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.STUN, {
          duration: 0, // ターン終了時まで
        }, '呪術狩りの封印鎖');
      }
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applySealingChain(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '封印する相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applySealingChain(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applySealingChain(validTargets[0].index);
    return true;
  },

  /**
   * C0000046: 泡沫の精霊
   * 基本技：このカードをリリースすると、次の水属性モンスターの召喚コストを2軽減する
   */
  C0000046: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, setMyGraveyard, myHand, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {

      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('対象のモンスターが存在しません', 'info');
        return false;
      }

      // 手札に水属性モンスターがいるかチェック
      const waterMonsters = myHand.filter(c => c.type === 'monster' && c.attribute === '水');
      if (waterMonsters.length === 0) {
        addLog('手札に水属性モンスターがいないため発動できません', 'info');
        return false;
      }

      // 自身を墓地に送る
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);
      addLog(`${monster.name}をリリース！`, 'info');

      // 手札のすべての水属性モンスターにコスト軽減を付与（次の1体のみ使用）
      setMyHand(prev => prev.map(c => {
        if (c.type === 'monster' && c.attribute === '水') {
          return {
            ...c,
            tempCostModifier: (c.tempCostModifier || 0) - 2,
            tempCostModifierSource: '泡沫の精霊',
            tempCostModifierOneTime: true, // 次の1体のみフラグ
          };
        }
        return c;
      }));
      addLog('次の水属性モンスターの召喚コストが2軽減！', 'heal');

      return true;
    }
    return false;
  },

  /**
   * C0000145: ブリザードキャット・スノウ
   * 基本技：相手の場にいるモンスター1体を指定し、その効果をターン終了時まで無効化。
   */
  C0000145: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applySilence = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        // SILENCE（効果無効）を付与（ターン終了時まで = duration: 0）
        applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.SILENCE, {
          duration: 0, // ターン終了時まで
        }, 'ブリザードキャット・スノウ');
        addLog(`${targetMonster.name}の効果をターン終了時まで無効化！`, 'info');
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applySilence(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '効果を無効化する相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applySilence(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      applySilence(validTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000144: ブリザードキャット・フロスト
   * 基本技：自身の攻撃力の半分のダメージを相手モンスター1体に与える。
   */
  C0000144: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {

      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'damage');
        return false;
      }

      // ダメージ量 = 攻撃力の半分
      const damage = Math.floor((monster.currentAttack ?? monster.attack ?? 0) / 2);

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 対象がいなくても技の発動自体は成功として扱う
      }

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        const target = validTargets[0];
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === target.index && m) {
            const newHp = m.currentHp - damage;
            addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${m.name}は破壊された！`, 'damage');
              return null;
            }
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `${damage}ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            setOpponentField(prev => prev.map((m, idx) => {
              if (idx === targetIndex && m) {
                const newHp = m.currentHp - damage;
                addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
                if (newHp <= 0) {
                  addLog(`${m.name}は破壊された！`, 'damage');
                  return null;
                }
                return { ...m, currentHp: newHp };
              }
              return m;
            }));
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      const target = validTargets[0];
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === target.index && m) {
          const newHp = m.currentHp - damage;
          addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
          if (newHp <= 0) {
            addLog(`${m.name}は破壊された！`, 'damage');
            return null;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));
      return true;
    }
    return false;
  },

  /**
   * C0000049: タイダルシフト
   * 場にいる水属性モンスター1体をデッキに戻し、デッキから同じコスト以下の別の水属性モンスターを場に出す（コスト不要）
   */
  C0000049: (skillText, context) => {
    // 魔法カードによる特殊召喚は召喚酔いなし（そのターン攻撃可能）
    const { addLog, setPendingMonsterTarget, setPendingDeckReview, summoningSickness = false } = context;
    const { myField, setMyField, myDeck, setMyDeck, currentPlayer } = getPlayerContext(context);

    // 自分の場の水属性モンスターを取得
    const waterMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null && monster.attribute === '水');

    if (waterMonsters.length === 0) {
      addLog('場に水属性モンスターがいません', 'info');
      return false;
    }

    // モンスターを選択してシフトする処理
    const performTidalShift = (selectedIndex: number) => {
      const selectedMonster = myField[selectedIndex];
      if (!selectedMonster) return;

      const targetCost = selectedMonster.cost;

      // デッキから同コスト以下の別の水属性モンスターを検索
      const eligibleMonsters = myDeck.filter(card =>
        card.type === 'monster' &&
        card.attribute === '水' &&
        card.cost <= targetCost &&
        card.id !== selectedMonster.id // 別のモンスター
      );

      if (eligibleMonsters.length === 0) {
        addLog(`コスト${targetCost}以下の別の水属性モンスターがデッキにいません`, 'info');
        // モンスターは戻さず効果失敗
        return;
      }

      // 選択したモンスターをデッキに戻す
      setMyField(prev => {
        const newField = [...prev];
        newField[selectedIndex] = null;
        return newField;
      });
      setMyDeck(prev => [...prev, selectedMonster]);
      addLog(`${selectedMonster.name}をデッキに戻した`, 'info');

      // 召喚処理
      const summonReplacement = (newMonster: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== newMonster.uniqueId));
        setMyField(prev => {
          const newField = [...prev];
          newField[selectedIndex] = {
            ...newMonster,
            currentHp: newMonster.hp ?? 0,
            currentAttack: newMonster.attack ?? 0,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            owner: currentPlayer,
          };
          return newField;
        });
        addLog(`タイダルシフト: デッキから「${newMonster.name}」を特殊召喚！`, 'heal');
      };

      // 1体のみの場合は自動選択
      if (eligibleMonsters.length === 1) {
        summonReplacement(eligibleMonsters[0]);
        return;
      }

      // 複数ある場合はデッキ選択UIを表示
      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: eligibleMonsters,
          title: 'タイダルシフト',
          message: `コスト${targetCost}以下の水属性モンスターを1体選択して召喚`,
          allowReorder: false,
          selectMode: {
            enabled: true,
            count: 1,
          },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) {
              summonReplacement(selectedCards[0]);
            }
          },
          onCancel: () => {
            addLog('タイダルシフト: 召喚をキャンセルしました', 'info');
          },
          selectionOwner: context.currentPlayer,
        });
      } else {
        // フォールバック: 最初の1体を召喚
        summonReplacement(eligibleMonsters[0]);
      }
    };

    // 水属性モンスターが1体のみの場合は自動選択
    if (waterMonsters.length === 1) {
      performTidalShift(waterMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'デッキに戻す水属性モンスターを選択',
        targetPlayer: 'self',
        validTargets: waterMonsters.map(t => t.index),
        callback: (selectedIndex: number) => {
          performTidalShift(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初の水属性モンスター
    performTidalShift(waterMonsters[0].index);
    return true;
  },

  /**
   * C0000047: マーメイドの恵み
   * 次の自分のターンのSPトークン増加量を1増加させる（1ターンに1度のみ）
   */
  C0000047: (skillText, context) => {
    const {
      addLog,
      currentPlayer,
      setP1NextTurnSPBonus,
      setP2NextTurnSPBonus,
    } = context;

    // 次のターンのSP増加ボーナスを+1
    if (currentPlayer === 1) {
      setP1NextTurnSPBonus((prev: number) => prev + 1);
    } else {
      setP2NextTurnSPBonus((prev: number) => prev + 1);
    }

    addLog('マーメイドの恵み: 次のターンのSP増加量+1！', 'heal');
    return true;
  },

  /**
   * C0000050: クラーケンの呼び声
   * 墓地の水属性モンスター1体を手札に戻し、そのカードの召喚コストを1軽減
   * 【刹那詠唱】
   */
  C0000050: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, setShowGraveyardViewer } = context;
    const { myGraveyard, setMyGraveyard, setMyHand, currentPlayer } = getPlayerContext(context);

    // 墓地の水属性モンスターを検索
    const waterMonsters = myGraveyard.filter(card =>
      card.type === 'monster' && card.attribute === '水'
    );

    if (waterMonsters.length === 0) {
      addLog('墓地に水属性モンスターがいません', 'info');
      return false;
    }

    // カードを手札に加える処理（コスト-1付き）
    const returnToHand = (targetCard: CardWithId) => {
      // 墓地から除去
      setMyGraveyard(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
      // 手札に加える（コスト-1を付与）
      const cardWithCostReduction = {
        ...targetCard,
        tempCostModifier: (targetCard.tempCostModifier || 0) - 1,
        tempCostModifierSource: 'クラーケンの呼び声',
      };
      setMyHand(prev => [...prev, cardWithCostReduction]);
      addLog(`クラーケンの呼び声: ${targetCard.name}を手札に戻した（召喚コスト-1）`, 'heal');
    };

    // 1体のみの場合は自動選択
    if (waterMonsters.length === 1) {
      returnToHand(waterMonsters[0]);
      return true;
    }

    // 複数いる場合は墓地選択UIを表示
    if (setPendingGraveyardSelection && setShowGraveyardViewer) {
      setShowGraveyardViewer(currentPlayer);
      setPendingGraveyardSelection({
        message: '手札に戻す水属性モンスターを選択',
        filter: (card: CardWithId) => card.type === 'monster' && card.attribute === '水',
        callback: (selectedCards: CardWithId[] | CardWithId | null) => {
          // GraveyardViewerModal は配列で渡すため、配列から取り出す
          const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
          if (targetCard) {
            returnToHand(targetCard);
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初の1体を選択
    returnToHand(waterMonsters[0]);
    return true;
  },

  /**
   * C0000331: アクアレギナの漂流船乗り
   * 基本技: このカードをリリースし、デッキから『アクアレギア』モンスター1体を手札に加える。
   */
  C0000331: (skillText, context) => {
    const { addLog, monsterIndex, setPendingDeckReview } = context;
    const { myField, setMyField, myDeck, setMyDeck, setMyHand, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('対象のモンスターが存在しません', 'info');
        return false;
      }

      // デッキから『アクアレギア』モンスターを検索
      const targetFilter = (card: CardWithId) =>
        card.type === 'monster' && card.name && card.name.includes('アクアレギア');
      const matchingCards = myDeck.filter(targetFilter);

      if (matchingCards.length === 0) {
        addLog('アクアレギナの漂流船乗り: デッキに『アクアレギア』モンスターがいません', 'info');
        return false;
      }

      // 自身をリリース
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);
      addLog(`${monster.name}をリリース！`, 'info');

      // サーチ処理
      const addToHand = (targetCard: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`アクアレギナの漂流船乗り: ${targetCard.name}を手札に加えた！`, 'heal');
      };

      // 1枚のみの場合は自動選択
      if (matchingCards.length === 1) {
        addToHand(matchingCards[0]);
        return true;
      }

      // 複数ある場合はデッキ選択UI
      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: matchingCards,
          title: 'アクアレギナの漂流船乗り',
          message: '手札に加える『アクアレギア』モンスターを1体選択',
          allowReorder: false,
          selectMode: { enabled: true, count: 1 },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) addToHand(selectedCards[0]);
          },
          onCancel: () => addLog('サーチをキャンセルしました', 'info'),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      addToHand(matchingCards[0]);
      return true;
    }
    return false;
  },

  /**
   * C0000332: アクアレギナの守護者
   * 基本技: 自分のライフを300回復。
   */
  C0000332: (skillText, context) => {
    if (context.skillType === 'basic') {
      healLife(context, 300, true);
      return true;
    }
    return false;
  },

  /**
   * C0000333: ヴェルゼファールの信徒・深みの儀式者
   * 基本技: 自分のライフを1000減らし、デッキから『ヴェルゼファール』モンスター1体を手札に加える。
   */
  C0000333: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand, myLife, setMyLife } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // ライフコストチェック
      if (myLife <= 1000) {
        addLog('ライフが足りません（コスト: 1000）', 'info');
        return false;
      }

      // デッキから『ヴェルゼファール』モンスターを検索
      const targetFilter = (card: CardWithId) =>
        card.type === 'monster' && card.name && card.name.includes('ヴェルゼファール');
      const matchingCards = myDeck.filter(targetFilter);

      if (matchingCards.length === 0) {
        addLog('深みの儀式者: デッキに『ヴェルゼファール』モンスターがいません', 'info');
        return false;
      }

      // ライフコストを支払う
      setMyLife(prev => prev - 1000);
      addLog('深みの儀式者: ライフを1000消費', 'damage');

      // サーチ処理
      const addToHand = (targetCard: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`深みの儀式者: ${targetCard.name}を手札に加えた！`, 'heal');
      };

      if (matchingCards.length === 1) {
        addToHand(matchingCards[0]);
        return true;
      }

      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: matchingCards,
          title: '深みの儀式者',
          message: '手札に加える『ヴェルゼファール』モンスターを1体選択',
          allowReorder: false,
          selectMode: { enabled: true, count: 1 },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) addToHand(selectedCards[0]);
          },
          onCancel: () => addLog('サーチをキャンセルしました', 'info'),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      addToHand(matchingCards[0]);
      return true;
    }
    return false;
  },

  /**
   * C0000334: ヴェルゼファールの眷属・クラディオム
   * 基本技：デッキからコスト4以下の《ヴェルゼファール》モンスターを手札に加える。
   */
  C0000334: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // デッキからコスト4以下の『ヴェルゼファール』モンスターを検索
      const targetFilter = (card: CardWithId) =>
        card.type === 'monster' &&
        card.name && card.name.includes('ヴェルゼファール') &&
        card.cost <= 4;
      const matchingCards = myDeck.filter(targetFilter);

      if (matchingCards.length === 0) {
        addLog('クラディオム: デッキにコスト4以下の『ヴェルゼファール』モンスターがいません', 'info');
        return false;
      }

      const addToHand = (targetCard: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`クラディオム: ${targetCard.name}を手札に加えた！`, 'heal');
      };

      if (matchingCards.length === 1) {
        addToHand(matchingCards[0]);
        return true;
      }

      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: matchingCards,
          title: 'クラディオム',
          message: '手札に加えるコスト4以下の『ヴェルゼファール』モンスターを選択',
          allowReorder: false,
          selectMode: { enabled: true, count: 1 },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) addToHand(selectedCards[0]);
          },
          onCancel: () => addLog('サーチをキャンセルしました', 'info'),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      addToHand(matchingCards[0]);
      return true;
    }
    return false;
  },

  /**
   * C0000335: ヴェルゼファールの眷属・シスラゴン
   * 基本技：このカードがリンク状態の時、デッキからコスト6以下の《ヴェルゼファール》モンスターを手札に加える。
   */
  C0000335: (skillText, context) => {
    const { addLog, monsterIndex, setPendingDeckReview } = context;
    const { myField, myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('対象のモンスターが存在しません', 'info');
        return false;
      }

      // リンク状態チェック
      if (!monster.linkedTo) {
        addLog('シスラゴン: リンク状態ではないため発動できません', 'info');
        return false;
      }

      // デッキからコスト6以下の『ヴェルゼファール』モンスターを検索
      const targetFilter = (card: CardWithId) =>
        card.type === 'monster' &&
        card.name && card.name.includes('ヴェルゼファール') &&
        card.cost <= 6;
      const matchingCards = myDeck.filter(targetFilter);

      if (matchingCards.length === 0) {
        addLog('シスラゴン: デッキにコスト6以下の『ヴェルゼファール』モンスターがいません', 'info');
        return false;
      }

      const addToHand = (targetCard: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`シスラゴン: ${targetCard.name}を手札に加えた！`, 'heal');
      };

      if (matchingCards.length === 1) {
        addToHand(matchingCards[0]);
        return true;
      }

      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: matchingCards,
          title: 'シスラゴン',
          message: '手札に加えるコスト6以下の『ヴェルゼファール』モンスターを選択',
          allowReorder: false,
          selectMode: { enabled: true, count: 1 },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) addToHand(selectedCards[0]);
          },
          onCancel: () => addLog('サーチをキャンセルしました', 'info'),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      addToHand(matchingCards[0]);
      return true;
    }
    return false;
  },

  /**
   * C0000336: ヴェルゼファールの眷属・ルミナクール
   * 基本技: 相手モンスター1体を次のターン終了まで行動不能にする。
   */
  C0000336: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('ルミナクール: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyStun = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        // STUN（行動不能）を付与（次のターン終了時まで = duration: 1）
        applyStatusToOpponentMonster(context, targetIndex, STATUS_EFFECT_TYPES.STUN, {
          duration: 1, // 次のターン終了時まで
        }, 'ルミナクール');
        addLog(`ルミナクール: ${targetMonster.name}を次のターン終了まで行動不能に！`, 'damage');
      };

      if (validTargets.length === 1) {
        applyStun(validTargets[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '行動不能にする相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => applyStun(selectedIndex),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyStun(validTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000337: ヴェルゼファールの眷属・タラッサロス
   * 基本技: このカードがリンク状態の時、相手フィールド全体に1500ダメージ。
   */
  C0000337: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('対象のモンスターが存在しません', 'info');
        return false;
      }

      // リンク状態チェック
      if (!monster.linkedTo) {
        addLog('タラッサロス: リンク状態ではないため発動できません', 'info');
        return false;
      }

      const opponentMonsters = opponentField.filter(m => m !== null);
      if (opponentMonsters.length === 0) {
        addLog('タラッサロス: 相手フィールドにモンスターがいません', 'info');
        return true;
      }

      // 全体に1500ダメージ
      const damage = 1500;
      addLog(`タラッサロス: 相手フィールド全体に${damage}ダメージ！`, 'damage');

      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newHp = m.currentHp - damage;
          addLog(`${m.name}に${damage}ダメージ（残りHP: ${Math.max(0, newHp)}）`, 'damage');
          if (newHp <= 0) {
            destroyedMonsters.push(m);
            return null;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));

      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        destroyedMonsters.forEach(m => addLog(`${m.name}が破壊された！`, 'damage'));
      }

      return true;
    }
    return false;
  },

  /**
   * C0000340: 深海の支配者・ヴェルゼファール
   * 上級技: 自分のライフを2000減らし、相手フィールド全体に3000ダメージ。
   */
  C0000340: (skillText, context) => {
    const { addLog } = context;
    const { myLife, setMyLife, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'advanced') {
      // ライフコストチェック
      if (myLife <= 2000) {
        addLog('ライフが足りません（コスト: 2000）', 'info');
        return false;
      }

      // ライフコストを支払う
      setMyLife(prev => prev - 2000);
      addLog('ヴェルゼファール: ライフを2000消費', 'damage');

      const opponentMonsters = opponentField.filter(m => m !== null);
      if (opponentMonsters.length === 0) {
        addLog('ヴェルゼファール: 相手フィールドにモンスターがいません', 'info');
        return true;
      }

      // 全体に3000ダメージ
      const damage = 3000;
      addLog(`ヴェルゼファール: 相手フィールド全体に${damage}ダメージ！`, 'damage');

      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newHp = m.currentHp - damage;
          addLog(`${m.name}に${damage}ダメージ（残りHP: ${Math.max(0, newHp)}）`, 'damage');
          if (newHp <= 0) {
            destroyedMonsters.push(m);
            return null;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));

      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        destroyedMonsters.forEach(m => addLog(`${m.name}が破壊された！`, 'damage'));
      }

      return true;
    }
    return false;
  },

  /**
   * C0000341: ヴェルゼファール降臨の儀式
   * 【刹那詠唱】
   * 自分の場にいる『ヴェルゼファール』モンスター1体をリリースし、
   * デッキから『ヴェルゼファール』モンスター1体を場に召喚（コスト不要）。
   * 召喚するモンスターのコストはリリースしたモンスターのコスト＋3以下。
   * 場に『深みの儀式者』がいる場合、召喚したモンスターの攻撃力をターン終了時まで800アップ。
   */
  C0000341: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingDeckReview, summoningSickness = false } = context;
    const { myField, setMyField, myDeck, setMyDeck, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    // 場のヴェルゼファールモンスターを取得
    const verzefaalMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('ヴェルゼファール'));

    if (verzefaalMonsters.length === 0) {
      addLog('降臨の儀式: 場に『ヴェルゼファール』モンスターがいません', 'info');
      return false;
    }

    // リリース後の召喚処理
    const performRitual = (releaseIndex: number) => {
      const releasedMonster = myField[releaseIndex];
      if (!releasedMonster) return;

      const maxCost = releasedMonster.cost + 3;

      // デッキからコスト条件を満たすヴェルゼファールを検索
      const eligibleMonsters = myDeck.filter(card =>
        card.type === 'monster' &&
        card.name && card.name.includes('ヴェルゼファール') &&
        card.cost <= maxCost
      );

      if (eligibleMonsters.length === 0) {
        addLog(`降臨の儀式: デッキにコスト${maxCost}以下の『ヴェルゼファール』モンスターがいません`, 'info');
        return;
      }

      // リリース
      setMyField(prev => {
        const newField = [...prev];
        newField[releaseIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, releasedMonster]);
      addLog(`降臨の儀式: ${releasedMonster.name}をリリース`, 'info');

      // 深みの儀式者がいるかチェック
      const hasRitualist = myField.some(m =>
        m && m.name && m.name.includes('深みの儀式者')
      );

      // 召喚処理
      const summonMonster = (target: CardWithId) => {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== target.uniqueId));
        setMyField(prev => {
          const newField = [...prev];
          // リリースしたスロットに召喚
          const atkBonus = hasRitualist ? 800 : 0;
          newField[releaseIndex] = {
            ...target,
            currentHp: target.hp ?? 0,
            currentAttack: (target.attack ?? 0) + atkBonus,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            owner: currentPlayer,
            atkBoostUntilEndOfTurn: hasRitualist ? 800 : 0, // ターン終了時リセット用
          };
          return newField;
        });
        if (hasRitualist) {
          addLog(`降臨の儀式: ${target.name}を召喚！（儀式者の力でATK+800）`, 'heal');
        } else {
          addLog(`降臨の儀式: ${target.name}を召喚！`, 'heal');
        }
      };

      if (eligibleMonsters.length === 1) {
        summonMonster(eligibleMonsters[0]);
        return;
      }

      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: eligibleMonsters,
          title: '降臨の儀式',
          message: `コスト${maxCost}以下の『ヴェルゼファール』モンスターを1体選択`,
          allowReorder: false,
          selectMode: { enabled: true, count: 1 },
          onSelect: (selectedCards: CardWithId[]) => {
            if (selectedCards.length > 0) summonMonster(selectedCards[0]);
          },
          onCancel: () => addLog('召喚をキャンセルしました', 'info'),
          selectionOwner: context.currentPlayer,
        });
      } else {
        summonMonster(eligibleMonsters[0]);
      }
    };

    // 1体のみの場合は自動選択
    if (verzefaalMonsters.length === 1) {
      performRitual(verzefaalMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'リリースする『ヴェルゼファール』モンスターを選択',
        targetPlayer: 'self',
        validTargets: verzefaalMonsters.map(v => v.index),
        callback: (selectedIndex: number) => performRitual(selectedIndex),
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    performRitual(verzefaalMonsters[0].index);
    return true;
  },

  /**
   * C0000342: 漂流民の抵抗
   * 自分の水属性モンスター1体に「守護」を与える。
   * 自分の『アクアレギナ』モンスター全体のHPを1000アップ。
   */
  C0000342: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 場の水属性モンスターを取得
    const waterMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.attribute === '水');

    if (waterMonsters.length === 0) {
      addLog('漂流民の抵抗: 場に水属性モンスターがいません', 'info');
      return false;
    }

    // 守護付与と全アクアレギナHP1000アップ処理
    const applyResistance = (targetIndex: number) => {
      setMyField(prev => prev.map((m, idx) => {
        if (!m) return null;

        const updates = { ...m };
        let logged = false;

        // 選択された水属性モンスターに守護を付与
        if (idx === targetIndex) {
          addLog(`${m.name}に「守護」を付与！`, 'info');
          updates.statusEffects = [
            ...(m.statusEffects || []),
            {
              type: STATUS_EFFECT_TYPES.GUARD,
              duration: -1, // 永続（使用時に解除）
              source: 'C0000342',
              sourceName: '漂流民の抵抗',
            },
          ];
          logged = true;
        }

        // アクアレギナモンスター全体のHPを1000アップ
        if (m.name && m.name.includes('アクアレギナ')) {
          if (!logged) {
            addLog(`${m.name}のHPを1000アップ！`, 'heal');
          } else {
            addLog(`${m.name}のHPを1000アップ！`, 'heal');
          }
          updates.hp = (m.hp ?? 0) + 1000;
          updates.currentHp = m.currentHp + 1000;
        }

        return updates;
      }));
    };

    // 1体のみの場合は自動選択
    if (waterMonsters.length === 1) {
      applyResistance(waterMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '「守護」を与える水属性モンスターを選択',
        targetPlayer: 'self',
        validTargets: waterMonsters.map(w => w.index),
        callback: (selectedIndex: number) => applyResistance(selectedIndex),
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyResistance(waterMonsters[0].index);
    return true;
  },

  /**
   * C0000343: アクアレギナの水晶術師の防壁
   * このターン、自分の『アクアレギナ』モンスターが受ける効果ダメージを0にし、HPを500アップ。
   * 場に『アクアレギナの動力-エテルノス・コア』がある場合、
   * 相手モンスター1体の攻撃力をターン終了時まで1000下げる。
   */
  C0000343: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, myFieldCard } = getPlayerContext(context);

    // 場のアクアレギナモンスターを取得
    const aquaReginaMonsters = myField.filter(m =>
      m && m.name && m.name.includes('アクアレギナ')
    );

    if (aquaReginaMonsters.length === 0) {
      addLog('水晶術師の防壁: 場に『アクアレギナ』モンスターがいません', 'info');
      return false;
    }

    // アクアレギナ全員にHP+500と効果ダメージ無効を付与
    setMyField(prev => prev.map(m => {
      if (m && m.name && m.name.includes('アクアレギナ')) {
        addLog(`${m.name}にHP+500と効果ダメージ無効を付与`, 'heal');
        return {
          ...m,
          hp: (m.hp ?? 0) + 500,
          currentHp: m.currentHp + 500,
          effectDamageImmunityUntilEndOfTurn: true,
        };
      }
      return m;
    }));

    // エテルノス・コアがあるかチェック（フィールドカード）
    const hasEternosCore = myFieldCard &&
      myFieldCard.name && myFieldCard.name.includes('アクアレギナの動力-エテルノス・コア');

    if (hasEternosCore) {
      // 相手モンスターを取得
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('水晶術師の防壁: 相手フィールドにモンスターがいません（ATK減少スキップ）', 'info');
        return true;
      }

      const applyAtkReduction = (targetIndex: number) => {
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const newAtk = Math.max(0, m.currentAttack - 1000);
            addLog(`エテルノス・コアの力: ${m.name}の攻撃力を1000ダウン！`, 'damage');
            return {
              ...m,
              currentAttack: newAtk,
              atkReducedUntilEndOfTurn: 1000,
            };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyAtkReduction(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を1000下げる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => applyAtkReduction(selectedIndex),
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyAtkReduction(validTargets[0].index);
    }

    return true;
  },

  /**
   * C0000052: ネプチューンの加護
   * このターン、自分の水属性モンスター全ての攻撃力を600アップ。
   * 次のターン終了時まで、自分の水属性モンスター全てへの技のダメージが0になる。
   */
  C0000052: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 場の水属性モンスターを取得
    const waterMonsters = myField.filter(m => m && m.attribute === '水');

    if (waterMonsters.length === 0) {
      addLog('ネプチューンの加護: 場に水属性モンスターがいません', 'info');
      return true; // 魔法発動自体は成功
    }

    // 水属性モンスター全体に効果を適用
    let buffedCount = 0;
    setMyField(prev => prev.map(m => {
      if (m && m.attribute === '水') {
        buffedCount++;
        return {
          ...m,
          currentAttack: m.currentAttack + 600,
          atkBoostUntilEndOfTurn: (m.atkBoostUntilEndOfTurn || 0) + 600,
          skillDamageImmunityUntilNextTurnEnd: true, // 次のターン終了時まで技ダメージ無効
        };
      }
      return m;
    }));

    addLog(`ネプチューンの加護: 水属性モンスター${buffedCount}体の攻撃力+600！`, 'heal');
    addLog('次のターン終了時まで水属性モンスターへの技ダメージが0になる！', 'heal');
    return true;
  },

  /**
   * C0000048: 人魚の波誘い
   * 【刹那詠唱】相手モンスター1体の攻撃先を別の相手モンスターに変更する。
   * ※攻撃リダイレクト効果として実装：相手モンスター2体を選び、前者が後者を攻撃する（同士討ち）
   */
  C0000048: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手フィールドのモンスターを取得
    const validMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validMonsters.length < 2) {
      addLog('人魚の波誘い: 相手フィールドにモンスターが2体以上必要です', 'info');
      return false;
    }

    // 戦闘処理を行う関数
    const performCombat = (attackerIndex: number, targetIndex: number) => {
      const attacker = opponentField[attackerIndex];
      const target = opponentField[targetIndex];

      if (!attacker || !target) {
        addLog('人魚の波誘い: 対象モンスターが存在しません', 'info');
        return;
      }

      // 攻撃者の攻撃力でダメージを与える
      const damage = attacker.currentAttack ?? attacker.attack ?? 0;
      const newTargetHp = target.currentHp - damage;

      addLog(`人魚の波誘い: ${attacker.name}が${target.name}を攻撃！${damage}ダメージ`, 'damage');

      setOpponentField((prev) => {
        const newField = [...prev];
        if (newTargetHp <= 0) {
          // 破壊
          addLog(`${target.name}が破壊された！`, 'damage');
          setOpponentGraveyard((g) => [...g, target]);
          newField[targetIndex] = null;
        } else {
          newField[targetIndex] = { ...target, currentHp: newTargetHp };
        }
        return newField;
      });
    };

    // ターゲット選択が使用可能な場合
    if (setPendingMonsterTarget) {
      // Step 1: 攻撃するモンスターを選択
      setPendingMonsterTarget({
        message: '攻撃させる相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (attackerIndex: number) => {
          // Step 2: 攻撃先モンスターを選択
          const remainingTargets = validMonsters.filter((t) => t.index !== attackerIndex);

          if (remainingTargets.length === 0) {
            addLog('人魚の波誘い: 攻撃先がありません', 'info');
            return;
          }

          if (remainingTargets.length === 1) {
            // 攻撃先が1体のみの場合は自動選択
            performCombat(attackerIndex, remainingTargets[0].index);
            return;
          }

          // 複数いる場合は選択
          setPendingMonsterTarget({
            message: '攻撃先の相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: (targetIndex: number) => {
              performCombat(attackerIndex, targetIndex);
            },
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初の2体で自動戦闘
    const [first, second] = validMonsters;
    performCombat(first.index, second.index);
    return true;
  },

  // ========================================
  // フェイズカード
  // ========================================

  /**
   * C0000339: アクアレギナの動力-エテルノス・コア（フェイズカード）
   * 3枚重ね: 相手フィールド全体に2000ダメージを与え、自分のライフを1000減らす。
   * その後、このカードは墓地に送られる。
   */
  C0000339: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard, setMyLife } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 相手フィールド全体に2000ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => {
        return prev.map(monster => {
          if (monster) {
            const newHp = (monster.currentHp ?? monster.hp ?? 0) - 2000;
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              return null;
            }
            return {
              ...monster,
              currentHp: newHp,
            };
          }
          return monster;
        });
      });

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
      }

      addLog(`アクアレギナの動力【3枚重ね】: 相手フィールド全体に2000ダメージ！${destroyedMonsters.length > 0 ? `（${destroyedMonsters.length}体破壊）` : ''}`, 'damage');

      // 自分のライフを1000減らす
      setMyLife(prev => prev - 1000);
      addLog('アクアレギナの動力【3枚重ね】: 自分のライフを1000減らす！', 'damage');

      return true;
    }

    return false;
  },

  /**
   * C0000143: 氷猫の使い魔
   * 基本技：次の「水属性」モンスターの召喚コストを1軽減。
   * ※墓地トリガーは cardTriggers/waterCards.js に実装済み
   */
  C0000143: (skillText, context) => {
    const { addLog, setNextSummonCostReduction } = context;

    if (context.skillType === 'basic') {
      // 次の水属性モンスター召喚コストを1軽減するフラグを立てる
      if (setNextSummonCostReduction) {
        setNextSummonCostReduction({
          attribute: '水',
          reduction: 1,
          source: '氷猫の使い魔',
        });
        addLog('氷猫の使い魔の基本技: 次の水属性モンスターの召喚コストを1軽減！', 'info');
      } else {
        // setNextSummonCostReductionがない場合のフォールバック
        addLog('氷猫の使い魔の基本技: 次の水属性モンスターの召喚コストを1軽減！（効果は手動で適用してください）', 'info');
      }
      return true;
    }
    return false;
  },

  /**
   * C0000146: 氷結の守護猫
   * 基本技：自分の《ブリザードキャット》モンスターのHPを300回復。
   * ※自壊時トリガーは cardTriggers/waterCards.js に実装済み
   */
  C0000146: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分の場の《ブリザードキャット》モンスターを取得
      const blizzardCats = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name && monster.name.includes('ブリザードキャット'));

      if (blizzardCats.length === 0) {
        addLog('場に《ブリザードキャット》モンスターがいません', 'info');
        return true;
      }

      // 全ての《ブリザードキャット》のHPを300回復
      let healedCount = 0;
      setMyField(prev => prev.map((m, idx) => {
        if (m && m.name && m.name.includes('ブリザードキャット')) {
          const maxHp = m.hp ?? 0;
          const currentHealth = m.currentHp ?? m.hp ?? 0;
          const newHp = Math.min(maxHp, currentHealth + 300);
          if (newHp > currentHealth) {
            healedCount++;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));

      addLog(`氷結の守護猫の基本技: 《ブリザードキャット》${blizzardCats.length}体のHPを300回復！`, 'heal');
      return true;
    }
    return false;
  },

  /**
   * C0000147: ブリザードキャット・シャード
   * 基本技：SP1払い、1枚ドローして1枚捨てる。
   * 上級技：このターン、《ブリザードキャット》は2回攻撃が可能。
   * ※召喚時トリガーは cardTriggers/waterCards.js に実装済み
   */
  C0000147: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myDeck, setMyDeck, myHand, setMyHand, myGraveyard, setMyGraveyard, myActiveSP, setMyActiveSP, myRestedSP, setMyRestedSP, myField, setMyField } = getPlayerContext(context);

    // 基本技：SP1払い、1枚ドローして1枚捨てる
    if (context.skillType === 'basic') {
      // SP1を消費
      if (myActiveSP < 1) {
        addLog('ブリザードキャット・シャードの基本技: SPが足りません', 'info');
        return false;
      }

      // デッキが空の場合
      if (myDeck.length === 0) {
        addLog('ブリザードキャット・シャードの基本技: デッキが空です', 'info');
        return false;
      }

      // SP1消費
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      // 1枚ドロー
      const drawnCard = { ...myDeck[0], uniqueId: `${myDeck[0].id}_${Date.now()}` };
      setMyDeck(prev => prev.slice(1));
      const newHand = [...myHand, drawnCard];
      setMyHand(newHand);

      addLog(`ブリザードキャット・シャードの基本技: ${drawnCard.name}をドロー！`, 'info');

      // 手札から1枚捨てる
      if (setPendingHandSelection && newHand.length > 0) {
        setPendingHandSelection({
          message: '捨てるカードを選択してください',
          count: 1,
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards && selectedCards.length > 0) {
              const discardCard = selectedCards[0];
              setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
              setMyGraveyard(prev => [...prev, discardCard]);
              addLog(`ブリザードキャット・シャードの基本技: ${discardCard.name}を捨てた`, 'info');
            }
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }

    // 上級技：このターン、《ブリザードキャット》は2回攻撃が可能
    if (context.skillType === 'advanced') {
      // 自分の場の《ブリザードキャット》を取得
      const blizzardCats = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name && monster.name.includes('ブリザードキャット'));

      if (blizzardCats.length === 0) {
        addLog('場に《ブリザードキャット》モンスターがいません', 'info');
        return true;
      }

      // 全ての《ブリザードキャット》に2回攻撃フラグを付与
      setMyField(prev => prev.map(m => {
        if (m && m.name && m.name.includes('ブリザードキャット')) {
          return { ...m, canDoubleAttack: true, attacksRemaining: 2 };
        }
        return m;
      }));

      addLog(`ブリザードキャット・シャードの上級技: 《ブリザードキャット》${blizzardCats.length}体が2回攻撃可能に！`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000149: ブリザードマスターの愛猫・ミスティ
   * 基本技：相手の場にいるモンスター全ての攻撃力をターン終了時まで半分にする。
   * ※常時効果・召喚時トリガーは別ファイルに実装済み
   */
  C0000149: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手の場のモンスターを取得
      const opponentMonsters = opponentField.filter(m => m !== null);

      if (opponentMonsters.length === 0) {
        addLog('相手の場にモンスターがいません', 'info');
        return true;
      }

      // 全ての相手モンスターの攻撃力を半分にする
      setOpponentField(prev => prev.map(m => {
        if (m) {
          const currentAtk = m.currentAttack ?? m.attack ?? 0;
          const newAttack = Math.floor(currentAtk / 2);
          addLog(`ミスティの基本技: ${m.name}の攻撃力を半減！（${currentAtk} → ${newAttack}）`, 'info');
          return {
            ...m,
            currentAttack: newAttack,
          };
        }
        return m;
      }));

      addLog(`ブリザードマスターの愛猫・ミスティの基本技: 相手モンスター${opponentMonsters.length}体の攻撃力を半減！`, 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000233: 水漏れのリリカ
   * 【召喚時】相手の場にいるモンスター1体を「濡れ」状態にする（cardTriggersで実装済み）
   * 基本技：自分のライフを300回復
   */
  C0000233: (skillText, context) => {
    const { addLog } = context;
    const { setMyLife } = getPlayerContext(context);

    // 基本技：ライフ300回復
    if (context.skillType === 'basic') {
      setMyLife(prev => prev + 300);
      addLog('水漏れのリリカの基本技: ライフを300回復した', 'heal');
      return true;
    }
    return false;
  },
  /**
   * C0000284: 白蛇の牙
   * 【毒侵】このカードが相手プレイヤーにダメージを与えた時、相手を「毒」状態にする。→ トリガーで実装
   * 基本技: 相手モンスター1体を「凍結」（攻撃半減・攻撃不可・技不可、ターン開始時に50％解除）にする。
   */
  C0000284: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：相手モンスター1体を凍結
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const applyFreeze = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            // 既存の状態異常に凍結を追加
            const statusEffects = m.statusEffects || [];
            const hasFreeze = statusEffects.some(s => s.type === STATUS_EFFECT_TYPES.FREEZE);
            if (hasFreeze) {
              addLog(`${m.name}は既に凍結状態です`, 'info');
              return m;
            }
            return {
              ...m,
              statusEffects: [
                ...statusEffects,
                {
                  type: STATUS_EFFECT_TYPES.FREEZE,
                  duration: -1, // 解除されるまで
                  removeChance: 0.5, // 50%で解除
                  source: 'C0000284',
                  sourceName: '白蛇の牙',
                },
              ],
            };
          }
          return m;
        }));
        addLog(`白蛇の牙の基本技: ${targetMonster.name}を凍結させた！`, 'info');
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyFreeze(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '凍結させる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyFreeze(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲット
      applyFreeze(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000363: 虹羽密林のアクアフェザー
   * 基本技: 相手モンスター1体の攻撃力をターン終了時まで1000下げる。この効果で攻撃力が0以下になった場合、そのモンスターを破壊する
   */
  C0000363: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('アクアフェザーの基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyDebuff = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        const newAtk = Math.max(0, (targetMonster.currentAttack ?? targetMonster.attack ?? 0) - 1000);
        addLog(`アクアフェザーの基本技: ${targetMonster.name}の攻撃力を1000ダウン！`, 'damage');

        if (newAtk <= 0) {
          addLog(`${targetMonster.name}は攻撃力0以下になり破壊された！`, 'damage');
          setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
          setOpponentGraveyard(prev => [...prev, targetMonster]);
        } else {
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              return {
                ...m,
                currentAttack: newAtk,
                atkReducedUntilEndOfTurn: (m.atkReducedUntilEndOfTurn || 0) + 1000,
              };
            }
            return m;
          }));
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDebuff(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を1000下げる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDebuff(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyDebuff(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000375: 虹羽密林の霧花蛙・ヴェルミナ
   * 基本技: 相手モンスター1体の攻撃力をターン終了時まで600下げる
   */
  C0000375: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('ヴェルミナの基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyDebuff = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        const newAtk = Math.max(0, (targetMonster.currentAttack ?? targetMonster.attack ?? 0) - 600);
        addLog(`ヴェルミナの基本技: ${targetMonster.name}の攻撃力を600ダウン！`, 'damage');

        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return {
              ...m,
              currentAttack: newAtk,
              atkReducedUntilEndOfTurn: (m.atkReducedUntilEndOfTurn || 0) + 600,
            };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDebuff(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を600下げる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDebuff(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyDebuff(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000379: 虹羽密林の青滴虫・サルフィス
   * 基本技: このカードを墓地に送り、次の自分のターン開始時まで、相手モンスターが受ける効果ダメージを200アップ
   */
  C0000379: (skillText, context) => {
    const { addLog, monsterIndex, setGameFlags } = context;
    const { myField, setMyField, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('サルフィスの基本技: 対象が見つかりません', 'info');
        return false;
      }

      // 自身を破壊して墓地に送る
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);
      addLog('サルフィスは自身を墓地に送った！', 'info');

      // 効果ダメージ増加フラグを設定（次の自分ターン開始時まで）
      // Note: setGameFlagsがある場合のみ設定（GameEngine統合時に有効）
      if (setGameFlags) {
        setGameFlags((prev: Record<string, unknown>) => ({
          ...prev,
          opponentEffectDamageBonus: {
            amount: 200,
            owner: currentPlayer,
            expiresAtOwnTurnStart: true,
          },
        }));
      }
      addLog('次の自分のターン開始時まで、相手モンスターへの効果ダメージ+200！', 'info');

      return true;
    }

    return false;
  },

  /**
   * C0000380: 虹羽密林の湖鱗獣・アクアレオン
   * 基本技: 自分のデッキの上から3枚を墓地に送り、その中に《虹羽密林》モンスターが含まれていた場合、自分のライフを800回復
   * 上級技: 自分のSPトークンを2個レスト状態にし、自分の墓地の《虹羽密林》モンスター1体を召喚。このターン中、そのモンスターの効果は無効化されない
   */
  C0000380: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, setShowGraveyardViewer, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myGraveyard, setMyGraveyard, myField, setMyField, setMyLife, myActiveSP, setMyActiveSP, setMyRestedSP, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // デッキの上から3枚を墓地に送る
      const milledCards = myDeck.slice(0, 3);
      if (milledCards.length === 0) {
        addLog('アクアレオンの基本技: デッキにカードがありません', 'info');
        return false;
      }

      setMyDeck(prev => prev.slice(milledCards.length));
      setMyGraveyard(prev => [...prev, ...milledCards]);

      milledCards.forEach(card => {
        addLog(`デッキから「${card.name}」を墓地に送った`, 'info');
      });

      // 虹羽密林モンスターが含まれているかチェック
      const hasRainbowMonster = milledCards.some(card =>
        card.type === 'monster' && card.name && card.name.includes('虹羽密林')
      );

      if (hasRainbowMonster) {
        setMyLife(prev => prev + 800);
        addLog('アクアレオンの基本技: 虹羽密林モンスターが含まれていた！ライフ800回復！', 'heal');
      }

      return true;
    }

    if (context.skillType === 'advanced') {
      // SP確認
      if (myActiveSP < 2) {
        addLog('アクアレオンの上級技: アクティブSPが2個以上必要です', 'info');
        return false;
      }

      // 墓地の虹羽密林モンスターを確認
      const rainbowMonstersInGraveyard = myGraveyard.filter(card =>
        card.type === 'monster' && card.name && card.name.includes('虹羽密林')
      );

      if (rainbowMonstersInGraveyard.length === 0) {
        addLog('アクアレオンの上級技: 墓地に《虹羽密林》モンスターがいません', 'info');
        return false;
      }

      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('アクアレオンの上級技: 場が満杯です', 'info');
        return false;
      }

      // SP消費
      setMyActiveSP(prev => prev - 2);
      setMyRestedSP(prev => prev + 2);
      addLog('アクアレオンの上級技: SPを2個消費！', 'info');

      const reviveMonster = (selectedCard: CardWithId) => {
        // 墓地から削除
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

        // 場に召喚（効果無効化されないフラグ付き）
        setMyField(prev => {
          const newField = [...prev];
          const newSlotIndex = newField.findIndex(slot => slot === null);
          if (newSlotIndex !== -1) {
            newField[newSlotIndex] = {
              ...selectedCard,
              currentHp: selectedCard.hp ?? 0,
              currentAttack: selectedCard.attack ?? 0,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              owner: currentPlayer,
              statusEffects: [],
              charges: [],
            };
          }
          return newField;
        });
        addLog(`アクアレオンの上級技: 墓地から「${selectedCard.name}」を復活！（効果無効化耐性付き）`, 'heal');
      };

      // 1体のみの場合は自動選択
      if (rainbowMonstersInGraveyard.length === 1) {
        reviveMonster(rainbowMonstersInGraveyard[0]);
        return true;
      }

      // 複数の場合は墓地選択UI
      if (setPendingGraveyardSelection && setShowGraveyardViewer) {
        setShowGraveyardViewer(currentPlayer);
        setPendingGraveyardSelection({
          message: '復活させる《虹羽密林》モンスターを選択',
          filter: (card: CardWithId) => card.type === 'monster' && card.name && card.name.includes('虹羽密林'),
          callback: (selectedCards: CardWithId[] | CardWithId | null) => {
            const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
            if (targetCard) {
              reviveMonster(targetCard);
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      reviveMonster(rainbowMonstersInGraveyard[0]);
      return true;
    }

    return false;
  },

  /**
   * C0000399: 呪術狩りの鎖術師ミリア
   * 基本技: SPトークン1個をレスト状態にし、相手の闇属性モンスター1体を次のターン終了時まで行動不能にする
   *         （ターンに1度）
   */
  C0000399: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myActiveSP, setMyActiveSP, setMyRestedSP, opponentField, setOpponentField } = getPlayerContext(context);
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    if (context.skillType === 'basic') {
      // SPが足りるか確認
      if (myActiveSP < 1) {
        addLog('呪術狩りの鎖術師ミリアの基本技: SPが足りません', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('呪術狩りの鎖術師ミリアの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // SPを消費
        setMyActiveSP(prev => prev - 1);
        setMyRestedSP(prev => prev + 1);
        addLog('呪術狩りの鎖術師ミリア: SPを1消費', 'info');

        // 行動不能を付与（次のターン終了時まで = duration 1）
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            const isDark = target.attribute === '闇';

            statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.STUN, {
              duration: 1, // 次のターン終了時まで
              source: 'C0000399',
              sourceName: '呪術狩りの鎖術師ミリア',
            });

            addLog(`呪術狩りの鎖術師ミリアの基本技: ${target.name}を行動不能に！${isDark ? '（闇属性）' : ''}`, 'info');
            newField[targetIndex] = { ...target };
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '行動不能にする相手モンスターを選択（闇属性に効果的）',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000403: 沼の血叫びヴォルガノス
   * 基本技: SPトークン1個をレスト状態にし、相手モンスター1体の攻撃力を次のターン終了時まで1000ダウン
   *         （ターンに1度）
   * 上級技: SPトークン1個をレスト状態にし、1枚ドローする（ターンに1度）
   */
  C0000403: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myActiveSP, setMyActiveSP, setMyRestedSP, myDeck, setMyDeck, setMyHand, opponentField, setOpponentField } = getPlayerContext(context);
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 基本技: 相手モンスターの攻撃力-1000
    if (context.skillType === 'basic') {
      // SPが足りるか確認
      if (myActiveSP < 1) {
        addLog('沼の血叫びヴォルガノスの基本技: SPが足りません', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('沼の血叫びヴォルガノスの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // SPを消費
        setMyActiveSP(prev => prev - 1);
        setMyRestedSP(prev => prev + 1);
        addLog('沼の血叫びヴォルガノス: SPを1消費', 'info');

        // 攻撃力-1000を付与（次のターン終了時まで）
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];

            // ATK_DOWNステータスを付与
            statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.ATK_DOWN, {
              value: 1000,
              expiresAfterEndPhases: 2, // 次のターン終了時まで
              source: 'C0000403',
              sourceName: '沼の血叫びヴォルガノス',
            });

            // currentAttackも更新
            const newAtk = Math.max(0, target.currentAttack - 1000);
            newField[targetIndex] = { ...target, currentAttack: newAtk };

            addLog(`沼の血叫びヴォルガノスの基本技: ${target.name}の攻撃力を1000ダウン！`, 'damage');
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を1000下げる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 上級技: 1枚ドロー
    if (context.skillType === 'advanced') {
      // SPが足りるか確認
      if (myActiveSP < 1) {
        addLog('沼の血叫びヴォルガノスの上級技: SPが足りません', 'info');
        return false;
      }

      // デッキがあるか確認
      if (myDeck.length === 0) {
        addLog('沼の血叫びヴォルガノスの上級技: デッキがありません', 'info');
        return false;
      }

      // SPを消費
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);
      addLog('沼の血叫びヴォルガノス: SPを1消費', 'info');

      // 1枚ドロー
      const drawnCard = myDeck[0];
      setMyDeck(prev => prev.slice(1));
      setMyHand(prev => [...prev, drawnCard]);
      addLog(`沼の血叫びヴォルガノスの上級技: ${drawnCard.name}をドロー！`, 'info');

      return true;
    }

    return false;
  },

  /**
   * C0000429: 黒涙のうねり
   * 【刹那詠唱】相手モンスター1体のHPを1000減らし、次のターン終了時までその攻撃力を300ダウン。
   */
  C0000429: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターを確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('黒涙のうねり: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 効果を適用する関数
    const applyEffect = (targetIndex: number) => {
      let destroyedMonster: MonsterInstance | null = null;

      setOpponentField((prev) => {
        const newField = [...prev];
        const target = newField[targetIndex];

        if (target) {
          const newHp = target.currentHp - 1000;
          const currentAtk = target.currentAttack ?? target.attack ?? 0;
          const newAtk = Math.max(0, currentAtk - 300);

          addLog(`黒涙のうねり: ${target.name}のHPを1000減少！攻撃力を300ダウン！`, 'damage');

          if (newHp <= 0) {
            destroyedMonster = target;
            addLog(`${target.name}は破壊された！`, 'damage');
            newField[targetIndex] = null;
          } else {
            newField[targetIndex] = {
              ...target,
              currentHp: newHp,
              currentAttack: newAtk,
              // 次のターン終了時まで攻撃力減少（2ターン持続）
              blackTearReduction: 300,
              blackTearTurnsRemaining: 2,
            };
          }
        }
        return newField;
      });

      if (destroyedMonster) {
        const dm = destroyedMonster as unknown as CardWithId;
        setOpponentGraveyard((prev) => [...prev, dm]);
      }
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '対象の相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000043: 深海のクラーケン
   * 基本技：1ターンに1度、場のコスト4以下の相手モンスター1体を指定し、「凍結」状態にする。
   */
  C0000043: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // コスト4以下の相手モンスターをフィルタ
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.cost <= 4);

      if (validTargets.length === 0) {
        addLog('コスト4以下の相手モンスターがいません', 'info');
        return true;
      }

      const applyFreeze = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        setOpponentField((prev) =>
          prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              // 既存の状態異常配列を取得または初期化
              const statusEffects = m.statusEffects ? [...m.statusEffects] : [];
              // 凍結を追加（攻撃力半減＋行動不能、50%で解除）
              statusEffects.push({
                type: STATUS_EFFECT_TYPES.FREEZE,
                duration: -1, // 永続（解除まで）
                removeChance: 0.5, // ターン開始時50%で解除
                source: 'C0000043',
                sourceName: '深海のクラーケン',
              });
              addLog(`深海のクラーケンの基本技: ${m.name}を凍結状態にした！`, 'info');
              return { ...m, statusEffects };
            }
            return m;
          })
        );
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyFreeze(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '凍結状態にするコスト4以下の相手モンスターを選択',
          targetPlayer: 'opponent',
          validTargets: validTargets.map((t) => t.index),
          callback: (selectedIndex: number) => {
            applyFreeze(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyFreeze(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000446: メイドマーメイド
   * 基本技: 自分モンスター1体のHPを600回復する。
   */
  C0000446: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 基本技: 自分モンスター1体のHPを600回復
    if (context.skillType === 'basic') {
      const myMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (myMonsters.length === 0) {
        addLog('メイドマーメイドの基本技: 自分のフィールドにモンスターがいません', 'info');
        return false;
      }

      const applyHeal = (targetIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const maxHp = target.hp || target.maxHp || target.currentHp;
            const newHp = Math.min(maxHp, target.currentHp + 600);
            addLog(`メイドマーメイドの基本技: ${target.name}のHPを600回復！（HP: ${target.currentHp} → ${newHp}）`, 'heal');
            newField[targetIndex] = { ...target, currentHp: newHp };
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (myMonsters.length === 1) {
        applyHeal(myMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'HPを600回復する自分のモンスターを選択',
          targetPlayer: 'self',
          callback: (selectedIndex: number) => {
            applyHeal(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyHeal(myMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000041: ダーク・リヴァイアサン
   * 基本技：次のターンの相手のSPトークン増加を1減少させる。
   * 召喚時効果は cardTriggers/waterCards.ts に実装済み
   */
  C0000041: (skillText, context) => {
    const { addLog, currentPlayer } = context;
    const { setOpponentStatusEffects, opponentStatusEffects } = context as typeof context & {
      setOpponentStatusEffects?: React.Dispatch<React.SetStateAction<import('../statusEffects/statusEngine').PlayerStatusEffect[]>>;
      opponentStatusEffects?: import('../statusEffects/statusEngine').PlayerStatusEffect[];
    };

    // 基本技：相手プレイヤーにSP増加減少を付与
    if (context.skillType === 'basic') {
      // contextにプレイヤー状態異常のsetterがある場合のみ実行
      if (setOpponentStatusEffects) {
        const result = statusEffectEngine.applyPlayerStatus(
          opponentStatusEffects || [],
          STATUS_EFFECT_TYPES.SP_GAIN_REDUCTION,
          {
            source: 'C0000041',
            sourceName: 'ダーク・リヴァイアサン',
            value: 1, // SP減少量
          }
        );

        if (result.success && result.statusEffect) {
          setOpponentStatusEffects((prev: PlayerStatusEffect[]) => [...prev, result.statusEffect!]);
          const opponentPlayer = currentPlayer === 1 ? 2 : 1;
          addLog(`ダーク・リヴァイアサンの基本技: プレイヤー${opponentPlayer}の次のターンSP増加-1！`, 'damage');
          return true;
        }
      }

      // フォールバック: contextにsetterがない場合でも成功として扱う
      addLog('ダーク・リヴァイアサンの基本技: 相手の次のターンSP増加-1！', 'damage');
      return true;
    }

    return false;
  },

  // ========================================
  // 深海領域 魔法カード
  // ========================================

  /**
   * C0000528: 深海への招き
   * 「深海領域」を開放し、デッキの［ディープシャドウ］モンスター1枚を深海領域に送る。
   */
  C0000528: (skillText, context) => {
    const { addLog, setPendingDeckReview, turn } = context;
    const { myDeck, setMyDeck, currentPlayer } = getPlayerContext(context);

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('深海への招き: 深海領域が利用できません', 'info');
      return false;
    }

    // 深海領域を開放
    if (!deepSeaZone.isUnlocked) {
      setDeepSeaZone(prev => ({ ...prev, isUnlocked: true }));
      addLog('深海への招き: 深海領域が開放された！', 'info');
    }

    // デッキの［ディープシャドウ］モンスターを取得
    const deepShadowMonsters = myDeck.filter(c =>
      c.type === 'monster' && c.category && c.category.includes('【ディープシャドウ】')
    );

    if (deepShadowMonsters.length === 0) {
      addLog('深海への招き: デッキに［ディープシャドウ］モンスターがいません', 'info');
      return true; // 深海領域の開放は成功したのでtrueを返す
    }

    const performSubmerge = (selectedCard: CardWithId) => {
      // デッキから削除
      setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

      // 深海領域に潜航（初期深度1）
      // 開放直後の場合は更新後のzoneを取得
      const currentZone = deepSeaZone.isUnlocked ? deepSeaZone : { ...deepSeaZone, isUnlocked: true };
      const result = submergeCard(currentZone, selectedCard, turn || 1, 1);
      if (result.success) {
        setDeepSeaZone(() => result.updatedZone);
        addLog(`深海への招き: ${selectedCard.name}を深海領域に送った！（深度1）`, 'info');
      } else {
        // 失敗時はデッキに戻す
        setMyDeck(prev => [...prev, selectedCard]);
        addLog(`深海への招き: ${result.message}`, 'info');
      }
    };

    // 1枚のみの場合は自動選択
    if (deepShadowMonsters.length === 1) {
      performSubmerge(deepShadowMonsters[0]);
      return true;
    }

    // 複数ある場合は選択UI
    if (setPendingDeckReview) {
      setPendingDeckReview({
        message: '深海領域に送る［ディープシャドウ］モンスターを選択',
        cards: deepShadowMonsters,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            performSubmerge(selectedCards[0]);
          }
        },
        selectionOwner: currentPlayer,
      });
      return true;
    }

    // フォールバック
    performSubmerge(deepShadowMonsters[0]);
    return true;
  },

  /**
   * C0000529: 海底からの呼び声
   * 深海領域のカード1枚を浮上させる。この浮上コストは1軽減される。
   */
  C0000529: (skillText, context) => {
    const { addLog, setPendingDeepSeaSelection, summoningSickness = true } = context;
    const { myField, setMyField, currentPlayer, myActiveSP, setMyActiveSP } = getPlayerContext(context);

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('海底からの呼び声: 深海領域が利用できません', 'info');
      return false;
    }

    if (deepSeaZone.cards.length === 0) {
      addLog('海底からの呼び声: 深海領域にカードがありません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('海底からの呼び声: 場に空きがありません', 'info');
      return false;
    }

    const performSurface = (deepSeaIndex: number) => {
      const deepSeaCard = deepSeaZone.cards[deepSeaIndex];
      const surfaceCost = getSurfaceCost(deepSeaCard, 1); // コスト-1軽減

      if (myActiveSP < surfaceCost) {
        addLog(`海底からの呼び声: SPが足りません（必要: ${surfaceCost}）`, 'info');
        return;
      }

      // 浮上処理
      const result = surfaceCard(deepSeaZone, deepSeaIndex, 1);
      if (!result.success || !result.surfacedCard) {
        addLog(`海底からの呼び声: ${result.message}`, 'info');
        return;
      }

      // SPを消費
      setMyActiveSP(prev => prev - surfaceCost);
      setDeepSeaZone(() => result.updatedZone);

      // 深度ボーナスを適用してフィールドに配置
      const bonus = result.bonus;
      const surfacedMonster: MonsterInstance = {
        ...result.surfacedCard,
        uniqueId: `${result.surfacedCard.id}_surface_${Date.now()}`,
        currentAttack: (result.surfacedCard.attack ?? 0) + bonus.attackBonus,
        currentHp: result.surfacedCard.hp ?? 0,
        canAttack: bonus.canPreemptiveStrike || !summoningSickness,
        charges: [],
        statusEffects: [],
        owner: currentPlayer,
        surfaceDepth: result.surfaceDepth,
      };

      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = surfacedMonster;
        }
        return newField;
      });

      addLog(`海底からの呼び声: ${result.surfacedCard.name}が浮上！深度${result.surfaceDepth}（コスト${surfaceCost}）`, 'info');
      if (bonus.attackBonus > 0) {
        addLog(`深度ボーナス: ATK+${bonus.attackBonus}${bonus.canPreemptiveStrike ? '、先制攻撃' : ''}`, 'info');
      }
    };

    // 1枚のみの場合は自動選択
    if (deepSeaZone.cards.length === 1) {
      performSurface(0);
      return true;
    }

    // 複数ある場合は選択UI
    if (setPendingDeepSeaSelection) {
      setPendingDeepSeaSelection({
        message: '浮上させるカードを選択（コスト-1）',
        cards: deepSeaZone.cards,
        count: 1,
        callback: (selectedIndices: number[]) => {
          if (selectedIndices && selectedIndices.length > 0) {
            performSurface(selectedIndices[0]);
          }
        },
        selectionOwner: currentPlayer,
      });
      return true;
    }

    // フォールバック
    performSurface(0);
    return true;
  },

  /**
   * C0000530: 深度加速
   * 深海領域の全カードに深度+2。
   */
  C0000530: (skillText, context) => {
    const { addLog } = context;

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('深度加速: 深海領域が利用できません', 'info');
      return false;
    }

    if (deepSeaZone.cards.length === 0) {
      addLog('深度加速: 深海領域にカードがありません', 'info');
      return false;
    }

    // 全カードの深度+2
    setDeepSeaZone(prev => ({
      ...prev,
      cards: prev.cards.map(card => ({
        ...card,
        depth: card.depth + 2,
      })),
    }));

    addLog(`深度加速: 深海領域の${deepSeaZone.cards.length}枚全ての深度+2！`, 'info');
    deepSeaZone.cards.forEach(c => {
      addLog(`${c.card.name}: 深度${c.depth} → ${c.depth + 2}`, 'info');
    });

    return true;
  },

  /**
   * C0000531: 荒海の強襲
   * 深海領域のカード1枚をコスト0で浮上させる。
   * そのカードはこのターン攻撃力が2倍になる。ターン終了時、そのカードを破壊する。
   */
  C0000531: (skillText, context) => {
    const { addLog, setPendingDeepSeaSelection, summoningSickness = true } = context;
    const { myField, setMyField, currentPlayer } = getPlayerContext(context);

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('荒海の強襲: 深海領域が利用できません', 'info');
      return false;
    }

    if (deepSeaZone.cards.length === 0) {
      addLog('荒海の強襲: 深海領域にカードがありません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('荒海の強襲: 場に空きがありません', 'info');
      return false;
    }

    const performAssault = (deepSeaIndex: number) => {
      // 浮上処理（コスト0）
      const result = surfaceCard(deepSeaZone, deepSeaIndex, 0);
      if (!result.success || !result.surfacedCard) {
        addLog(`荒海の強襲: ${result.message}`, 'info');
        return;
      }

      setDeepSeaZone(() => result.updatedZone);

      // 深度ボーナス + ATK2倍
      const bonus = result.bonus;
      const baseAttack = (result.surfacedCard.attack ?? 0) + bonus.attackBonus;
      const doubledAttack = baseAttack * 2;

      const surfacedMonster: MonsterInstance = {
        ...result.surfacedCard,
        uniqueId: `${result.surfacedCard.id}_assault_${Date.now()}`,
        currentAttack: doubledAttack,
        currentHp: result.surfacedCard.hp ?? 0,
        canAttack: true, // 強襲なので攻撃可能
        charges: [],
        statusEffects: [],
        owner: currentPlayer,
        surfaceDepth: result.surfaceDepth,
        destroyAtEndOfTurn: true, // ターン終了時に破壊
      };

      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          newField[slot] = surfacedMonster;
        }
        return newField;
      });

      addLog(`荒海の強襲: ${result.surfacedCard.name}がコスト0で浮上！ATK${baseAttack}→${doubledAttack}（2倍）`, 'info');
      addLog('ターン終了時、このカードは破壊される', 'info');
    };

    // 1枚のみの場合は自動選択
    if (deepSeaZone.cards.length === 1) {
      performAssault(0);
      return true;
    }

    // 複数ある場合は選択UI
    if (setPendingDeepSeaSelection) {
      setPendingDeepSeaSelection({
        message: '強襲で浮上させるカードを選択（コスト0、ATK2倍、ターン終了時破壊）',
        cards: deepSeaZone.cards,
        count: 1,
        callback: (selectedIndices: number[]) => {
          if (selectedIndices && selectedIndices.length > 0) {
            performAssault(selectedIndices[0]);
          }
        },
        selectionOwner: currentPlayer,
      });
      return true;
    }

    // フォールバック
    performAssault(0);
    return true;
  },

  /**
   * C0000532: レヴィアタンの大時化
   * 「深海領域」を開放し、デッキのランダムな水属性カードを2枚を深海領域に送る。
   */
  C0000532: (skillText, context) => {
    const { addLog, turn } = context;
    const { myDeck, setMyDeck } = getPlayerContext(context);

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('レヴィアタンの大時化: 深海領域が利用できません', 'info');
      return false;
    }

    // 深海領域を開放
    if (!deepSeaZone.isUnlocked) {
      setDeepSeaZone(prev => ({ ...prev, isUnlocked: true }));
      addLog('レヴィアタンの大時化: 深海領域が開放された！', 'info');
    }

    // デッキの水属性カードを取得（モンスターに限らない）
    const waterCards = myDeck.filter(c => c.attribute === '水');

    if (waterCards.length === 0) {
      addLog('レヴィアタンの大時化: デッキに水属性カードがありません', 'info');
      return true; // 深海領域の開放は成功したのでtrueを返す
    }

    // 空き容量を確認（開放直後の場合を考慮）
    const currentZone = deepSeaZone.isUnlocked ? deepSeaZone : { ...deepSeaZone, isUnlocked: true };
    const availableCapacity = currentZone.maxCapacity - currentZone.cards.length;
    if (availableCapacity <= 0) {
      addLog('レヴィアタンの大時化: 深海領域が満杯です', 'info');
      return true; // 深海領域の開放は成功したのでtrueを返す
    }

    // ランダムに最大2枚選択
    const shuffled = [...waterCards].sort(() => Math.random() - 0.5);
    const toSubmerge = shuffled.slice(0, Math.min(2, availableCapacity));

    // 選択したカードをデッキから除去
    const submergeIds = new Set(toSubmerge.map(c => c.uniqueId));
    setMyDeck(prev => prev.filter(c => !submergeIds.has(c.uniqueId)));

    // 深海領域に追加
    let updatedZone = currentZone;
    toSubmerge.forEach(card => {
      const result = submergeCard(updatedZone, card, turn || 1, 1);
      if (result.success) {
        updatedZone = result.updatedZone;
      }
    });
    setDeepSeaZone(() => updatedZone);

    addLog(`レヴィアタンの大時化: デッキから${toSubmerge.length}枚を深海領域へ！`, 'info');
    toSubmerge.forEach(c => addLog(`${c.name}が深海へ潜航！（深度1）`, 'info'));

    return true;
  },

  /**
   * C0000527: 荒海王ヴェルゼファール
   * 基本技: 深海領域の全カードに深度+1。
   */
  C0000527: (skillText, context) => {
    // 基本技のみ処理
    if (!skillText.includes('基本技')) {
      return false;
    }

    const { addLog } = context;

    // 深海領域の取得
    const deepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
    const setDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

    if (!deepSeaZone || !setDeepSeaZone) {
      addLog('ヴェルゼファール基本技: 深海領域が利用できません', 'info');
      return false;
    }

    if (deepSeaZone.cards.length === 0) {
      addLog('ヴェルゼファール基本技: 深海領域にカードがありません', 'info');
      return false;
    }

    // 全カードの深度+1
    setDeepSeaZone(prev => ({
      ...prev,
      cards: prev.cards.map(card => ({
        ...card,
        depth: card.depth + 1,
      })),
    }));

    addLog(`ヴェルゼファール基本技: 深海領域の${deepSeaZone.cards.length}枚全ての深度+1！`, 'info');
    deepSeaZone.cards.forEach(c => {
      addLog(`${c.card.name}: 深度${c.depth} → ${c.depth + 1}`, 'info');
    });

    return true;
  },

  // =============================================================================
  // 深海領域サポート魔法カード
  // =============================================================================

  /**
   * C0000552: 海を舐めた末路
   * 相手モンスター1体を破壊する。
   * このカードのコストは深海領域のカード枚数分、軽減される。
   * ※コスト軽減は magic-spirit.tsx の getModifiedCostInfo で処理
   */
  C0000552: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('海を舐めた末路: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const destroyMonster = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      addLog(`海を舐めた末路: ${targetMonster.name}を破壊！`, 'damage');

      // 墓地に送る
      setOpponentGraveyard(prev => [...prev, targetMonster]);

      // フィールドから除去
      setOpponentField(prev => prev.map((m, idx) =>
        idx === targetIndex ? null : m
      ));
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      destroyMonster(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊する相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          destroyMonster(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初のターゲット
    destroyMonster(validTargets[0].index);
    return true;
  },
};
